import { memo, useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Typography,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";

import {
    createBatchTransport,
    getOwners,
    getTransportsHistory,
    resolveTransportAnimals,
} from "../../api/index.js";
import Form from "../../components/Form.js";
import Spinner from "../../components/Spinner.js";
import { BackButton } from "../../components/Buttons.js";
import useSelectStyles from "../../components/styles/Select.js";
import { useSnackbar } from "../../utils/index.js";
import TransportsHistoryPdf from "./TransportsHistoryPdf.js";

const parseAnimalCodes = (input) => {
    const flockCodes = new Set();
    const registrationCodes = new Set();
    const invalidCodes = [];
    const parts = input.split(",").map((part) => part.trim()).filter(Boolean);

    for (const part of parts) {
        if (part.includes("-")) {
            const rangeMatch = part.match(/^(\d{4})\s*-\s*(\d{4})$/);

            if (!rangeMatch) {
                invalidCodes.push(part);
                continue;
            }

            const start = Number.parseInt(rangeMatch[1], 10);
            const end = Number.parseInt(rangeMatch[2], 10);
            if (start > end) {
                invalidCodes.push(part);
                continue;
            }

            for (let code = start; code <= end; code += 1) {
                flockCodes.add(code.toString().padStart(4, "0"));
            }
        } else if (/^\d{4}$/.test(part)) {
            flockCodes.add(part);
        } else if (/^\d{12}$/.test(part)) {
            registrationCodes.add(part);
        } else {
            invalidCodes.push(part);
        }
    }

    return {
        flockCodes: [...flockCodes],
        registrationCodes: [...registrationCodes],
        invalidCodes,
    };
};

const AnimalTransfer = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [owners, setOwners] = useState([]);
    const [step, setStep] = useState(1);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [selectionDraft, setSelectionDraft] = useState(null);
    const [automaticAnimals, setAutomaticAnimals] = useState([]);
    const [duplicateGroups, setDuplicateGroups] = useState([]);
    const [selectedDuplicateIds, setSelectedDuplicateIds] = useState([]);
    const [selectedAnimals, setSelectedAnimals] = useState([]);
    const [currentStatus, setCurrentStatus] = useState(null);
    const [transportsData, setTransportsData] = useState([]);

    const classes = useSelectStyles();
    const { success, error } = useSnackbar();

    const fetchOwners = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getOwners();
            setOwners(response.owners || []);
        } catch (requestError) {
            error(requestError);
        }
        setIsLoading(false);
    }, [error]);

    useEffect(() => {
        fetchOwners();
    }, [fetchOwners]);

    const ownerOptions = owners.map((owner) => ({
        value: `${owner.code} - ${owner.lastName} ${owner.firstName}`,
        text: `${owner.code} - ${owner.lastName} ${owner.firstName}`,
    }));

    const selectionFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownerOptions,
            placeholder: "Εκτροφή αποστολής",
            required: true,
            defaultValue: selectionDraft?.owner || "",
        },
        {
            customType: "input",
            id: "animalCodes",
            type: "text",
            multiline: true,
            minRows: 4,
            placeholder: "Κωδικοί ζώων",
            helperText: "4-ψήφιοι ή 12-ψήφιοι κωδικοί και εύρη, χωρισμένα με κόμμα",
            required: true,
            value: selectionDraft?.animalCodes || "",
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Συνέχεια",
        },
    ];

    const resetFlow = () => {
        setStep(1);
        setSelectedOwner(null);
        setSelectionDraft(null);
        setAutomaticAnimals([]);
        setDuplicateGroups([]);
        setSelectedDuplicateIds([]);
        setSelectedAnimals([]);
        setCurrentStatus(null);
        setTransportsData([]);
    };

    const handleSelectionSubmit = async (values) => {
        setIsLoading(true);
        setSelectionDraft(values);

        try {
            const owner = owners.find((candidate) => (
                `${candidate.code} - ${candidate.lastName} ${candidate.firstName}` === values.owner
            ));
            if (!owner) {
                error("Η εκτροφή αποστολής δεν είναι έγκυρη");
                return;
            }

            if (!values.animalCodes?.trim()) {
                error("Παρακαλώ εισάγετε τουλάχιστον έναν κωδικό ζώου");
                return;
            }

            const parsedCodes = parseAnimalCodes(values.animalCodes);
            if (parsedCodes.invalidCodes.length > 0) {
                error(`Οι ακόλουθοι κωδικοί δεν είναι έγκυροι: ${parsedCodes.invalidCodes.join(", ")}`);
                return;
            }

            const response = await resolveTransportAnimals(owner._id, {
                flockCodes: parsedCodes.flockCodes,
                registrationCodes: parsedCodes.registrationCodes,
            });
            if (!response.success) {
                error(response.message);
                return;
            }

            if (response.missingCodes?.length > 0) {
                error(`Τα ακόλουθα ζώα δεν βρέθηκαν: ${response.missingCodes.join(", ")}. Δεν έγινε καμία μεταφορά.`);
                return;
            }

            setSelectedOwner(owner);
            setAutomaticAnimals(response.automaticAnimals || []);
            setDuplicateGroups(response.duplicateGroups || []);
            setSelectedDuplicateIds(response.preselectedDuplicateAnimalIds || []);

            if (response.duplicateGroups?.length > 0) {
                setStep(2);
            } else {
                setSelectedAnimals(response.automaticAnimals || []);
                setStep(3);
            }
        } catch (requestError) {
            error(requestError);
        } finally {
            setIsLoading(false);
        }
    };

    const allDuplicateAnimalIds = duplicateGroups.flatMap((group) => (
        group.animals.map((animal) => animal.id)
    ));
    const areAllDuplicateAnimalsSelected = allDuplicateAnimalIds.length > 0
        && allDuplicateAnimalIds.every((animalId) => selectedDuplicateIds.includes(animalId));

    const handleDuplicateSelection = (animalId) => {
        setSelectedDuplicateIds((currentIds) => (
            currentIds.includes(animalId)
                ? currentIds.filter((id) => id !== animalId)
                : [...currentIds, animalId]
        ));
    };

    const handleSelectAllDuplicates = () => {
        setSelectedDuplicateIds(areAllDuplicateAnimalsSelected ? [] : allDuplicateAnimalIds);
    };

    const handleDuplicateConfirmation = () => {
        const duplicateAnimals = duplicateGroups.flatMap((group) => (
            group.animals
                .filter((animal) => selectedDuplicateIds.includes(animal.id))
                .map((animal) => ({ ...animal, flockCode: group.flockCode }))
        ));
        const animals = [...automaticAnimals, ...duplicateAnimals];

        if (animals.length === 0) {
            error("Δεν επιλέχθηκαν ζώα για μεταφορά");
            return;
        }

        setSelectedAnimals(animals);
        setStep(3);
    };

    const receivingOwnerOptions = ownerOptions.filter((option) => (
        option.value !== `${selectedOwner?.code} - ${selectedOwner?.lastName} ${selectedOwner?.firstName}`
    ));
    const transferFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: receivingOwnerOptions,
            placeholder: "Νέα μονάδα στην οποία θα μεταφερθούν τα ζώα",
            required: true,
        },
        {
            customType: "date-picker",
            id: "transferDate",
            type: "desktop",
            label: "Ημ/νία Μεταφοράς",
            width: "595px",
            background: "third",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή Μεταφοράς",
        },
    ];
    const historyFormContent = [
        {
            customType: "dropdown",
            id: "animalRegistrationCode",
            items: selectedAnimals.map((animal) => ({
                value: animal.registrationCode,
                text: animal.registrationCode,
            })),
            placeholder: "Ζώο για προβολή ιστορικού",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Προβολή Ιστορικού",
        },
    ];

    const handleTransferSubmit = async (values) => {
        setIsLoading(true);
        try {
            const receivingOwner = owners.find((owner) => (
                `${owner.code} - ${owner.lastName} ${owner.firstName}` === values.owner
            ));
            if (!receivingOwner) {
                error("Η εκτροφή παραλαβής δεν είναι έγκυρη");
                return;
            }

            const response = await createBatchTransport({
                sendOwner: selectedOwner._id,
                animalIds: selectedAnimals.map((animal) => animal.id),
                receiveOwner: receivingOwner._id,
                transferDate: values.transferDate,
            });
            if (response.success) {
                success(response.message);
                resetFlow();
            } else {
                error(response.message);
            }
        } catch (requestError) {
            error(requestError);
        } finally {
            setIsLoading(false);
        }
    };

    const handleHistorySubmit = async (values) => {
        setIsLoading(true);
        try {
            const historyAnimal = selectedAnimals.find((animal) => (
                animal.registrationCode === values.animalRegistrationCode
            ));
            if (!historyAnimal) {
                error("Το επιλεγμένο ζώο δεν είναι έγκυρο");
                return;
            }

            const response = await getTransportsHistory(selectedOwner._id, historyAnimal.id);
            if (response.success) {
                setCurrentStatus(response.transports.currentStatus);
                setTransportsData(response.transports.transportData);
                setStep(4);
            } else {
                error(response.message);
            }
        } catch (requestError) {
            error(requestError);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Spinner open={isLoading} />
            {step === 1 && (
                <Box className={classes.outerBox}>
                    <Box container flexDirection="column" className={classes.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Μαζική Μεταφορά Ζώων
                        </Typography>
                        <Box sx={{ margin: "1rem", padding: "1rem", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                            <Typography variant="body2" sx={{ marginBottom: "0.5rem" }}>
                                • Εισάγετε 4-ψήφιους κωδικούς εκτροφής ή 12-ψήφιους κωδικούς γενεαλογικού βιβλίου.
                            </Typography>
                            <Typography variant="body2" sx={{ marginBottom: "0.5rem" }}>
                                • Χωρίστε τους κωδικούς με κόμμα και χρησιμοποιήστε εύρη όπως 0356-0423.
                            </Typography>
                            <Typography variant="body2">
                                • Αν ένας 4-ψήφιος κωδικός αντιστοιχεί σε περισσότερα ζώα, θα επιλέξετε τα σωστά πριν τη μεταφορά.
                            </Typography>
                        </Box>
                        <Form content={selectionFormContent} onSubmit={handleSelectionSubmit} toResetForm={false} />
                    </Box>
                </Box>
            )}

            {step === 2 && (
                <Box className={classes.outerBox}>
                    <Box container flexDirection="column" className={classes.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Βρέθηκαν διπλότυποι κωδικοί εκτροφής
                        </Typography>
                        <Typography variant="body1" sx={{ margin: "0 1rem 1rem" }}>
                            Επιλέξτε ποια ζώα θέλετε να μεταφερθούν.
                        </Typography>
                        {automaticAnimals.length > 0 && (
                            <Typography variant="body2" sx={{ margin: "0 1rem 1rem" }}>
                                Τα ζώα με μοναδικό κωδικό έχουν συμπεριληφθεί αυτόματα.
                            </Typography>
                        )}
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleSelectAllDuplicates}
                            sx={{ width: "300px", marginBottom: "1rem" }}
                        >
                            {areAllDuplicateAnimalsSelected ? "Αποεπιλογή όλων" : "Επιλογή όλων"}
                        </Button>
                        {duplicateGroups.map((group) => (
                            <Box
                                key={group.flockCode}
                                sx={{
                                    width: "100%",
                                    maxWidth: "595px",
                                    marginBottom: "1rem",
                                    padding: "1rem",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                    {`Κωδικός Εκτροφής: ${group.flockCode}`}
                                </Typography>
                                {group.animals.map((animal) => (
                                    <FormControlLabel
                                        key={animal.id}
                                        sx={{ display: "block", marginLeft: 0 }}
                                        control={(
                                            <Checkbox
                                                checked={selectedDuplicateIds.includes(animal.id)}
                                                onChange={() => handleDuplicateSelection(animal.id)}
                                            />
                                        )}
                                        label={animal.registrationCode}
                                    />
                                ))}
                            </Box>
                        ))}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleDuplicateConfirmation}
                            sx={{ width: "300px", marginBottom: "1rem" }}
                        >
                            Επιβεβαίωση Επιλογής
                        </Button>
                        <BackButton onClick={() => setStep(1)} />
                    </Box>
                </Box>
            )}

            {step === 3 && (
                <Box className={classes.outerBox}>
                    <Box container flexDirection="column" className={classes.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            {`Μεταφορά ${selectedAnimals.length} ζώων`}
                        </Typography>
                        <Typography variant="body2" sx={{ margin: "0 1rem 1rem", textAlign: "center" }}>
                            {selectedAnimals.map((animal) => animal.registrationCode).join(", ")}
                        </Typography>
                        <Form content={transferFormContent} onSubmit={handleTransferSubmit} toResetForm={false} />
                        <Box sx={{ width: "100%", maxWidth: "600px", marginTop: "2rem" }}>
                            <Typography variant="h6" sx={{ marginBottom: "0.5rem", textAlign: "center" }}>
                                Ιστορικό μεταφορών ζώου
                            </Typography>
                            <Form content={historyFormContent} onSubmit={handleHistorySubmit} toResetForm={false} />
                        </Box>
                        <BackButton onClick={() => setStep(duplicateGroups.length > 0 ? 2 : 1)} />
                    </Box>
                </Box>
            )}

            {step === 4 && (
                <>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={() => setStep(3)}
                        sx={{ marginBottom: "1rem" }}
                    >
                        Πίσω
                    </Button>
                    <TransportsHistoryPdf currentStatus={currentStatus} transports={transportsData} />
                </>
            )}
        </>
    );
};

export default memo(AnimalTransfer)
